import React, { useEffect, useRef } from "react";

interface Props {
  className?: string;
  delay?: number;
}

/**
 * Wraps content in a scroll-reveal container: fades/slides in the first time
 * it enters the viewport. Falls back to always-visible when
 * IntersectionObserver is unavailable; prefers-reduced-motion is handled in
 * CSS (src/index.scss).
 */
const Reveal: React.FC<Props> = ({ className, delay, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      el.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal${className ? ` ${className}` : ""}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
};

export default Reveal;
